import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    linkedSignal,
    output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { QpxButton, QpxCheckbox, QpxTypography } from '../../directives';
import { QpxHeroIcon } from '../hero-icon';

@Component({
    selector: 'qpx-recipient-import-selection',
    imports: [
        MatButtonModule,
        MatCheckboxModule,
        QpxButton,
        QpxCheckbox,
        QpxHeroIcon,
        QpxTypography,
    ],
    templateUrl: './recipient-import-selection.html',
    styleUrl: './recipient-import-selection.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        role: 'region',
        '[attr.aria-labelledby]': 'headingId()',
        '[attr.aria-describedby]': 'descriptionId()',
    },
})
export class QpxRecipientImportSelection {
    //#region Inputs
    public readonly candidates = input.required<readonly string[]>();
    public readonly selectionLimit = input.required<number>();
    public readonly disabled = input(false);
    public readonly id = input.required<string>();
    public readonly title = input('Choose who to import');
    public readonly description = input<string>();
    public readonly cancelLabel = input('Cancel');
    public readonly confirmLabel = input<string>();
    //#endregion

    //#region Outputs
    public readonly confirmed = output<readonly string[]>();
    public readonly cancelled = output<void>();
    //#endregion

    //#region State
    protected readonly selectedCandidates = linkedSignal({
        source: this.candidates,
        computation: () => new Set<string>(),
    });
    protected readonly effectiveSelectionLimit = computed(() => {
        const limit = this.selectionLimit();
        return Number.isFinite(limit) ? Math.max(0, Math.floor(limit)) : 0;
    });
    protected readonly selectedCount = computed(
        () => this.selectedCandidates().size
    );
    protected readonly canConfirm = computed(() => {
        const selectedCount = this.selectedCount();
        return (
            !this.disabled() &&
            selectedCount > 0 &&
            selectedCount <= this.effectiveSelectionLimit()
        );
    });
    protected readonly headingId = computed(() => `${this.id()}-heading`);
    protected readonly descriptionId = computed(
        () => `${this.id()}-description`
    );
    protected readonly effectiveDescription = computed(
        () =>
            this.description() ??
            `Not every recipient will fit. Select up to ${this.effectiveSelectionLimit()}.`
    );
    protected readonly effectiveConfirmLabel = computed(
        () => this.confirmLabel() ?? `Add ${this.selectedCount()} selected`
    );
    protected readonly selectionSummary = computed(() => {
        const count = this.selectedCount();
        const limit = this.effectiveSelectionLimit();
        const recipientLabel = count === 1 ? 'recipient' : 'recipients';
        return `${count} ${recipientLabel} selected. Selection limit is ${limit}.`;
    });
    //#endregion

    //#region Template methods
    protected candidateId(index: number): string {
        return `${this.id()}-candidate-${index}`;
    }

    protected isCandidateSelected(candidate: string): boolean {
        return this.selectedCandidates().has(candidate);
    }

    protected isCandidateDisabled(candidate: string): boolean {
        return (
            this.disabled() ||
            (!this.isCandidateSelected(candidate) &&
                this.selectedCount() >= this.effectiveSelectionLimit())
        );
    }

    protected toggleCandidate(candidate: string, selected: boolean): void {
        if (this.disabled()) return;

        this.selectedCandidates.update((current) => {
            const next = new Set(current);
            if (selected) {
                if (next.size < this.effectiveSelectionLimit()) {
                    next.add(candidate);
                }
            } else {
                next.delete(candidate);
            }
            return next;
        });
    }

    protected cancel(): void {
        if (this.disabled()) return;

        this.selectedCandidates.set(new Set());
        this.cancelled.emit();
    }

    protected confirm(): void {
        if (!this.canConfirm()) return;

        const selectedCandidates = this.candidates().filter((candidate) =>
            this.selectedCandidates().has(candidate)
        );
        this.confirmed.emit(selectedCandidates);
    }
    //#endregion
}
